import { useOptionalDependency } from 'ditox-react';
import { useSubscription } from 'observable-hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { EMPTY } from 'rxjs';
import { useShallow } from 'zustand/react/shallow';
import AppCell from '@/components/common/app-cell';
import { AvatarWithStatus } from '@/components/common/avatar-with-status';
import { INTERCOM_MANAGER_TOKEN } from '@/container';
import useSignOut from '@/hooks/users/use-sign-out';
import type { AuthType } from '@/models/auth';
import ROUTES from '@/routes';
import { useAuthStore } from '@/stores/auth';
import { useMessageStore } from '@/stores/message';
import { queryClient } from '@/utils/query-client';

export default function SwitchUserRoute() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const intercomManager = useOptionalDependency(INTERCOM_MANAGER_TOKEN);
  const credentials = useAuthStore(useShallow((s) => Array.from(s.credentials.values())));
  const [credentialsInUse, setCredentialsInUse] = useState<number[]>([]);
  const { mutate: signOut } = useSignOut();

  const handleSwitchUser = (credential: AuthType) => {
    useAuthStore.getState().start(credential);
    navigate(ROUTES.MESSAGES.$path(), { replace: true });
  };

  const handleAllSignOut = () => {
    signOut({ credentials });
    navigate(ROUTES.AUTH.SIGN_IN.$path(), { replace: true });
  };

  useSubscription(intercomManager?.event$ ?? EMPTY, (event) => {
    switch (event.event) {
      case 'auth-credential-in-use':
        {
          const userId = event.data?.userId;
          if (userId) {
            setCredentialsInUse((s) => s.concat([userId]));
          }
        }
        break;
    }
  });

  useEffect(() => {
    intercomManager?.dispatch({ event: 'auth-credential-in-use' });
  }, []);

  return (
    <div className='w-full px-5 max-w-screen-xs mx-auto'>
      <ul className='bg-primary-content flex flex-col p-4 gap-4 items-stretch rounded shadow-sm max-h-60vh 4xl:max-h-40vh overflow-y-auto'>
        {credentials.map((credential) => (
          <li
            key={credential.UserID}
            className='group hover:bg-primary/10 rounded-lg transition-colors'>
            <button
              type='button'
              className='w-full'
              onClick={() => handleSwitchUser(credential)}>
              <AppCell
                prefix={
                  <AvatarWithStatus
                    className='size-12'
                    size={[48, 48]}
                    imageUrl={credential.AvatarUrl}
                  />
                }
                label={credential.NickName}
                suffix={
                  credentialsInUse.includes(credential.UserID) ? (
                    <span className='group-hover:text-primary text-body2 text-secondary transition-colors'>
                      {t('auth.switch.user-in-use')}
                    </span>
                  ) : null
                }
                chevron={!credentialsInUse.includes(credential.UserID)}
              />
            </button>
          </li>
        ))}
      </ul>

      <Link
        className='btn w-full btn-ghost bg-neutral-0 hover:text-primary-content mt-4'
        to={ROUTES.AUTH.SIGN_IN.$path()}
        state={{ from: 'switch' }}>
        {t('auth.switch.sign-in-different-user')}
      </Link>

      <button
        type='button'
        className='btn w-full btn-ghost text-primary-content mt-4'
        onClick={handleAllSignOut}>
        {t('auth.switch.all-sign-out')}
      </button>
    </div>
  );
}
